import { Box, Stack, Typography, Divider, Skeleton } from "@mui/material";

const Overrides = ({ data, loading }) => {
	if (loading || !data) {
		return (
			<Box
				sx={{
					flex: 1,
					p: "16px 18px",
					backgroundColor: "background.paper",
					border: "1px solid rgba(29,84,162,0.1)",
					borderRadius: "12px",
					boxShadow: "0 1px 8px rgba(29,84,162,0.06)",
				}}
			>
				<Skeleton variant="text" width="40%" height={32} />
				<Skeleton variant="text" width="60%" height={16} />
				<Skeleton variant="text" width="30%" height={16} sx={{ mb: 1 }} />
				<Divider sx={{ my: 1.25 }} />
				<Skeleton variant="text" height={24} />
				<Skeleton variant="text" height={24} />
				<Skeleton variant="text" height={24} />
			</Box>
		);
	}

	const {
		total = 0,
		last_month_total = 0,
		total_percentage = 0,
		breakdown = {},
		percentage = {},
		payout_status = "",
		period,
		previous_period,
	} = Object.values(data ?? {})[0];

	const isPositive = total_percentage >= 0;

	const formatPeriod = (value) => {
		if (!value) return "";

		const [year, month] = value.split("-");
		return new Date(Number(year), Number(month) - 1).toLocaleString("default", {
			month: "short",
			year: "numeric",
		});
	};

	const levels = [
		{
			label: "L1 Earnings",
			value: breakdown.l1_earnings ?? 0,
			change: percentage.l1_earnings ?? 0,
		},
		{
			label: "L2 Earnings",
			value: breakdown.l2_earnings ?? 0,
			change: percentage.l2_earnings ?? 0,
		},
		{
			label: "L3 Earnings",
			value: breakdown.l3_earnings ?? 0,
			change: percentage.l3_earnings ?? 0,
		},
	];

	return (
		<Box
			sx={{
				flex: 1,
				p: "16px 18px",
				backgroundColor: "background.paper",
				border: "1px solid rgba(29,84,162,0.1)",
				borderRadius: "12px",
				boxShadow: "0 1px 8px rgba(29,84,162,0.06)",
			}}
		>
			<Stack direction="row" alignItems="baseline" gap={1} mb={0.5}>
				<Typography variant="h6" fontWeight={500}>
					${total.toFixed(2)}
				</Typography>

				<Box
					sx={{
						fontSize: "0.68rem",
						fontWeight: 500,
						px: 1,
						py: 0.25,
						borderRadius: "20px",
						backgroundColor: isPositive ? "#EAF3DE" : "#FCEBEB",
						color: isPositive ? "#3B6D11" : "#A32D2D",
					}}
				>
					{isPositive ? "+" : ""}
					{total_percentage}%
				</Box>
			</Stack>

			<Typography sx={{ fontSize: "0.68rem", color: "text.secondary", mb: 1 }}>
				{formatPeriod(period)} vs ${last_month_total.toFixed(2)} ({formatPeriod(previous_period)})
			</Typography>

			<Stack direction="row" alignItems="center" gap={0.75} mb={1.25}>
				<Box
					sx={{
						width: 6,
						height: 6,
						borderRadius: "50%",
						backgroundColor: payout_status === "Paid" ? "#2E7D32" : "#ED6C02",
					}}
				/>
				<Typography
					sx={{
						fontSize: "0.68rem",
						fontWeight: 500,
						color: payout_status === "Paid" ? "#2E7D32" : "#ED6C02",
					}}
				>
					{payout_status}
				</Typography>
			</Stack>

			<Box sx={{ my: 1.25 }}>
				<Divider />
			</Box>

			{levels.map(({ label, value, change }) => {
				const positive = change >= 0;

				return (
					<Stack key={label} direction="row" justifyContent="space-between" alignItems="center" py={0.6}>
						<Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>{label}</Typography>

						<Stack direction="row" alignItems="center" gap={1}>
							<Typography
								sx={{
									fontSize: "0.68rem",
									color: change === 0 ? "text.secondary" : positive ? "#3B6D11" : "#A32D2D",
								}}
							>
								{positive ? "+" : ""}
								{change}%
							</Typography>

							<Typography sx={{ fontSize: "0.8rem", fontWeight: 500 }}>${value.toFixed(2)}</Typography>
						</Stack>
					</Stack>
				);
			})}
		</Box>
	);
};

export default Overrides;
